import { useTranslation } from "react-i18next";
import type { WhatsAppMessageTemplate, TemplateButton } from "@/hooks/useWhatsAppMessageTemplates";

interface TemplatePreviewProps {
  template: Partial<WhatsAppMessageTemplate> & { bodyText: string };
  variableValues?: Record<number, string>;
}

function interpolate(text: string, values: Record<number, string>): string {
  return text.replace(/\{\{(\d+)\}\}/g, (_, n) => values[Number(n)] ?? `{{${n}}}`);
}

export function TemplatePreview({ template, variableValues = {} }: TemplatePreviewProps) {
  const { t } = useTranslation("whatsappTemplates");

  const body = interpolate(template.bodyText, variableValues);
  const footer = template.footerText ? interpolate(template.footerText, variableValues) : null;
  const headerText =
    template.headerType === "TEXT" && template.headerText
      ? interpolate(template.headerText, variableValues)
      : null;

  const buttons: TemplateButton[] = Array.isArray(template.buttons) ? template.buttons : [];

  return (
    <div className="flex flex-col items-center">
      <p className="text-xs text-muted-foreground mb-2">{t("previewTitle")}</p>
      {/* Phone mockup */}
      <div className="w-64 rounded-3xl border-4 border-border bg-background shadow-lg overflow-hidden">
        {/* Status bar */}
        <div className="bg-[#075E54] h-14 flex items-end px-3 pb-2">
          <span className="text-white text-xs font-semibold truncate">WhatsApp Business</span>
        </div>
        {/* Chat bg */}
        <div
          className="min-h-40 p-3"
          style={{ background: "repeating-linear-gradient(135deg,#e5ddd520 0 4px,transparent 0 8px),#ECE5DD" }}
        >
          {/* Bubble */}
          <div className="bg-white rounded-lg shadow-sm max-w-[90%] overflow-hidden">
            {/* Header */}
            {headerText && (
              <div className="bg-muted px-3 py-2 border-b">
                <p className="text-xs font-semibold leading-snug">{headerText}</p>
              </div>
            )}
            {template.headerType && template.headerType !== "TEXT" && (
              <div className="bg-muted h-20 flex items-center justify-center border-b">
                <span className="text-muted-foreground text-xs">
                  {template.headerType === "IMAGE" && "🖼 Imagem"}
                  {template.headerType === "VIDEO" && "🎬 Vídeo"}
                  {template.headerType === "DOCUMENT" && "📄 Documento"}
                </span>
              </div>
            )}
            {/* Body */}
            <div className="px-3 py-2">
              <p className="text-xs leading-relaxed whitespace-pre-wrap">{body}</p>
            </div>
            {/* Footer */}
            {footer && (
              <div className="px-3 pb-2">
                <p className="text-[10px] text-muted-foreground">{footer}</p>
              </div>
            )}
            {/* Timestamp */}
            <div className="px-3 pb-1 flex justify-end">
              <span className="text-[9px] text-muted-foreground">agora ✓✓</span>
            </div>
          </div>
          {/* Quick reply buttons */}
          {buttons.filter((b) => b.type === "QUICK_REPLY").length > 0 && (
            <div className="flex flex-col gap-1 mt-1 max-w-[90%]">
              {buttons
                .filter((b) => b.type === "QUICK_REPLY")
                .map((b, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg shadow-sm text-center py-1 px-2"
                  >
                    <span className="text-xs text-[#128C7E] font-medium">{b.text}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
